import { useEffect, useState, useCallback } from 'react';
import { IAuditConfig } from '@skystone/shared';
import { auditApi } from '@/services/audit-api';
import { cn, getErrorMessage } from '@/lib/utils';
import { PageLoading, ErrorBanner } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';

function ToggleSwitch({
  enabled,
  onChange,
  disabled,
  label,
}: Readonly<{
  enabled: boolean;
  onChange: (value: boolean) => void;
  disabled: boolean;
  label: string;
}>) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        enabled ? 'bg-primary' : 'bg-muted',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition duration-200 ease-in-out',
          enabled ? 'translate-x-5' : 'translate-x-0'
        )}
      />
    </button>
  );
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  CREATE_USER: 'User Created',
};

export function AuditConfigTab() {
  const [configs, setConfigs] = useState<IAuditConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const loadConfigs = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditApi.getConfigs();
      setConfigs(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load audit configuration'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleToggle = async (
    eventType: string,
    field: 'auditEnabled' | 'slackEnabled',
    value: boolean
  ): Promise<void> => {
    setUpdating(eventType);
    setError(null);
    try {
      const response = await auditApi.updateConfig(eventType, { [field]: value });
      setConfigs((prev) =>
        prev.map((c) => (c.eventType === eventType ? response : c))
      );
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to update configuration'));
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Enable or disable audit logging and Slack notifications per event type.
      </p>

      <ErrorBanner message={error} />

      {configs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No audit event types configured yet. Events will be logged with default settings
            (audit enabled, Slack disabled) until configured here.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {configs.map((config) => (
            <Card key={config.eventType}>
              <CardHeader>
                <CardTitle className="text-lg">
                  {EVENT_TYPE_LABELS[config.eventType] || config.eventType}
                </CardTitle>
                <CardDescription>
                  Event type: <code className="text-xs">{config.eventType}</code>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
                  <div className="flex items-center gap-3">
                    <ToggleSwitch
                      enabled={config.auditEnabled}
                      onChange={(value) => handleToggle(config.eventType, 'auditEnabled', value)}
                      disabled={updating === config.eventType}
                      label={`Toggle audit logging for ${config.eventType}`}
                    />
                    <span className="text-sm font-medium text-foreground">Audit Logging</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ToggleSwitch
                      enabled={config.slackEnabled}
                      onChange={(value) => handleToggle(config.eventType, 'slackEnabled', value)}
                      disabled={updating === config.eventType}
                      label={`Toggle Slack notifications for ${config.eventType}`}
                    />
                    <span className="text-sm font-medium text-foreground">Slack Notifications</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
